import { LSelect } from "./ui/LSelect";
import { useState } from "react";
import { GripVertical, Plus, Trash2, Zap, Filter, Award } from "lucide-react";

// Visual, drag-to-reorder rule builder. Controlled: { trigger, match, conditions[], actions[] }.
export default function RuleBuilder({ value, onChange, catalog }) {
  const v = value;
  const cat = catalog || { triggers: [], fields: [], operators: [], actions: [], matchModes: [] };
  const [dragCtx, setDragCtx] = useState(null); // { kind, index }

  const set = (patch) => onChange({ ...v, ...patch });

  const fieldMeta = (f) => cat.fields.find((x) => x.value === f) || {};
  const actionMeta = (t) => cat.actions.find((x) => x.value === t) || {};

  // ----- conditions -----
  const addCondition = () =>
    set({ conditions: [...v.conditions, { field: cat.fields[0]?.value || "amount", operator: "gte", value: "" }] });
  const updCondition = (i, patch) =>
    set({ conditions: v.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const delCondition = (i) => set({ conditions: v.conditions.filter((_, idx) => idx !== i) });

  // ----- actions -----
  const addAction = () =>
    set({ actions: [...v.actions, { type: cat.actions[0]?.value || "award_points", value: "" }] });
  const updAction = (i, patch) =>
    set({ actions: v.actions.map((a, idx) => (idx === i ? { ...a, ...patch } : a)) });
  const delAction = (i) => set({ actions: v.actions.filter((_, idx) => idx !== i) });

  // ----- drag reorder -----
  const onDrop = (kind, dropIndex) => {
    if (!dragCtx || dragCtx.kind !== kind) return;
    const list = kind === "cond" ? [...v.conditions] : [...v.actions];
    const [moved] = list.splice(dragCtx.index, 1);
    list.splice(dropIndex, 0, moved);
    set(kind === "cond" ? { conditions: list } : { actions: list });
    setDragCtx(null);
  };

  const triggerLabel = cat.triggers.find((t) => t.value === v.trigger?.event)?.label || v.trigger?.event;

  return (
    <div className="space-y-4" data-testid="rule-builder">
      {/* TRIGGER */}
      <div className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-4">
        <div className="flex items-center gap-2 mb-2"><Zap className="h-4 w-4 text-sky-500" /><span className="text-xs font-bold uppercase tracking-wider text-sky-600">When (Trigger)</span></div>
        <LSelect value={v.trigger?.event || "purchase"} onChange={(e) => set({ trigger: { ...v.trigger, event: e.target.value } })}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="rule-trigger-select">
          {cat.triggers.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </LSelect>
      </div>

      {/* CONDITIONS */}
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Filter className="h-4 w-4 text-amber-500" /><span className="text-xs font-bold uppercase tracking-wider text-amber-600">If (Conditions)</span></div>
          <div className="flex items-center gap-2">
            <LSelect value={v.match} onChange={(e) => set({ match: e.target.value })} className="rounded-lg border border-border bg-background px-2 py-1 text-xs" data-testid="rule-match-select">
              {cat.matchModes.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </LSelect>
            <button onClick={addCondition} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-800" data-testid="rule-add-condition"><Plus className="h-3.5 w-3.5" /> Add</button>
          </div>
        </div>
        {v.conditions.length === 0 ? (
          <p className="text-xs text-muted-foreground italic py-2">No conditions — rule applies to every trigger.</p>
        ) : (
          <div className="space-y-2">
            {v.conditions.map((c, i) => (
              <div key={i} draggable onDragStart={() => setDragCtx({ kind: "cond", index: i })}
                onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop("cond", i)}
                className="flex items-center gap-2 bg-card border border-border rounded-lg p-2" data-testid={`rule-condition-${i}`}>
                <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab shrink-0" />
                <LSelect value={c.field} onChange={(e) => updCondition(i, { field: e.target.value })} className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs">
                  {cat.fields.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </LSelect>
                <LSelect value={c.operator} onChange={(e) => updCondition(i, { operator: e.target.value })} className="rounded-md border border-border bg-background px-2 py-1.5 text-xs">
                  {cat.operators.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </LSelect>
                <input value={c.value} onChange={(e) => updCondition(i, { value: e.target.value })}
                  type={fieldMeta(c.field).valueType === "number" ? "number" : "text"}
                  className="w-24 rounded-md border border-border bg-background px-2 py-1.5 text-xs" placeholder="value" />
                <button onClick={() => delCondition(i)} className="p-1 text-destructive hover:bg-destructive/10 rounded shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ACTIONS */}
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Award className="h-4 w-4 text-emerald-500" /><span className="text-xs font-bold uppercase tracking-wider text-emerald-600">Then (Actions)</span></div>
          <button onClick={addAction} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800" data-testid="rule-add-action"><Plus className="h-3.5 w-3.5" /> Add</button>
        </div>
        {v.actions.length === 0 ? (
          <p className="text-xs text-rose-600 italic py-2">At least one action is required.</p>
        ) : (
          <div className="space-y-2">
            {v.actions.map((a, i) => (
              <div key={i} draggable onDragStart={() => setDragCtx({ kind: "act", index: i })}
                onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop("act", i)}
                className="flex items-center gap-2 bg-card border border-border rounded-lg p-2" data-testid={`rule-action-${i}`}>
                <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab shrink-0" />
                <LSelect value={a.type} onChange={(e) => updAction(i, { type: e.target.value })} className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs">
                  {cat.actions.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                </LSelect>
                <input value={a.value} onChange={(e) => updAction(i, { value: e.target.value })}
                  className="w-28 rounded-md border border-border bg-background px-2 py-1.5 text-xs" placeholder={actionMeta(a.type).param || "value"} />
                <button onClick={() => delAction(i)} className="p-1 text-destructive hover:bg-destructive/10 rounded shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SUMMARY */}
      <div className="rounded-lg bg-muted/50 border border-border p-3 text-xs text-foreground" data-testid="rule-summary">
        <span className="font-semibold">Summary: </span>
        When <span className="text-sky-600 font-medium">{triggerLabel}</span>
        {v.conditions.length > 0 && <> and <span className="text-amber-600 font-medium">{v.match === "all" ? "all" : "any"}</span> of {v.conditions.length} condition(s) match</>}
        , then {v.actions.map((a, i) => (
          <span key={i}><span className="text-emerald-600 font-medium">{actionMeta(a.type).label || a.type}{a.value ? ` (${a.value})` : ""}</span>{i < v.actions.length - 1 ? ", " : ""}</span>
        ))}.
      </div>
    </div>
  );
}
